import type {
  ReservationSlotTargetShift,
  ReservationSlotSummary,
  ReservationSlotGenerationResult,
} from '../../domain/reservationSlots';
import type {
  ReservationSlotRepository,
  GenerateReservationSlotsInput,
} from '../../application/reservationSlots/ports/ReservationSlotRepository';
import {
  reservationSlotApi,
  type TargetShiftApiItem,
  type SummaryApiItem,
  type GenerateApiResponse,
} from '../api/reservationSlotApi';

/**
 * 生成対象シフトAPIレスポンス（snake_case）をドメイン型（camelCase）へ変換する
 */
function targetShiftToDomain(item: TargetShiftApiItem): ReservationSlotTargetShift {
  return {
    doctorShiftId: item.doctor_shift_id,
    date: item.date,
    startTime: item.start_time,
    endTime: item.end_time,
    doctorId: item.doctor_id,
    doctorName: item.doctor_name,
    sourceTemplateId: item.source_template_id,
  };
}

/**
 * サマリAPIレスポンス（snake_case）をドメイン型（camelCase）へ変換する
 */
function summaryToDomain(item: SummaryApiItem): ReservationSlotSummary {
  return {
    year: item.year,
    month: item.month,
    totalSlots: item.total_slots,
    categoryCounts: item.category_counts,
  };
}

/**
 * 生成結果APIレスポンス（snake_case）をドメイン型（camelCase）へ変換する
 */
function generationResultToDomain(res: GenerateApiResponse): ReservationSlotGenerationResult {
  return {
    createdCount: res.created_count,
    categoryCounts: res.category_counts,
  };
}

/**
 * 解放枠 Repository 具象実装
 *
 * @implements {ReservationSlotRepository}
 */
export class ReservationSlotRepositoryImpl implements ReservationSlotRepository {
  async findTargetShifts(year: number, month: number): Promise<ReservationSlotTargetShift[]> {
    const items = await reservationSlotApi.getTargetShifts(year, month);
    return items.map(targetShiftToDomain);
  }

  async generate(input: GenerateReservationSlotsInput): Promise<ReservationSlotGenerationResult> {
    const res = await reservationSlotApi.generate({
      year: input.year,
      month: input.month,
      assignments: input.assignments.map((a) => ({
        doctor_shift_id: a.doctorShiftId,
        template_id: a.templateId,
      })),
    });
    return generationResultToDomain(res);
  }

  async findSummaries(): Promise<ReservationSlotSummary[]> {
    const items = await reservationSlotApi.getSummary();
    return items.map(summaryToDomain);
  }
}

export const reservationSlotRepository = new ReservationSlotRepositoryImpl();
